import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const CookiePolicyPage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Cookie Policy</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Cookie Policy
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* 1. Introduction */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. What Are Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Cookies are small text files stored on your device when you visit a website. They serve
          various purposes including remembering your preferences, maintaining your session, and
          helping us understand how you use our platform. This Cookie Policy explains what cookies
          AmudFin S.A. uses, why we use them, and how you can control them.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          This policy is issued in compliance with Directive 2002/58/EC of the European Parliament
          and of the Council (the ePrivacy Directive), as transposed into Luxembourg law by the Law
          of 30 May 2005 on the protection of privacy in the electronic communications sector, and
          the GDPR where cookies constitute personal data processing.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 2. Types of Cookies */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Types of Cookies We Use
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We categorise our cookies into three groups. We do not use any third-party advertising or
          tracking cookies. AmudFin does not participate in cross-site tracking or behavioural
          advertising.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          2.1 Strictly Necessary Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          These cookies are essential for the platform to function. They cannot be disabled. Without
          these cookies, services you have requested (such as logging in) cannot be provided. Under
          Article 5(3) of the ePrivacy Directive, these cookies are exempt from the consent
          requirement.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          2.2 Functional Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          These cookies remember your preferences and settings to provide a more personalised
          experience, such as your preferred language, dashboard layout, and table column
          configurations. They do not track your activity across other websites.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          2.3 Analytics Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          These cookies help us understand how users interact with the platform by collecting
          anonymised usage statistics. All analytics data is aggregated and cannot be used to
          identify individual users. These cookies are set only with your consent.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 3. Cookie Inventory */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Cookie Inventory
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cookie Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>amudfin_session</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Maintains your authenticated session and prevents unauthorised access to your account</TableCell>
                <TableCell>Session (expires on browser close)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_csrf</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Cross-Site Request Forgery protection token, ensures form submissions originate from our platform</TableCell>
                <TableCell>Session</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_auth</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Stores the JWT access token for API authentication</TableCell>
                <TableCell>15 minutes (auto-refreshed)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_refresh</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Refresh token used to obtain new access tokens without re-authentication</TableCell>
                <TableCell>7 days</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_2fa_pending</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Temporary flag indicating two-factor authentication is in progress</TableCell>
                <TableCell>5 minutes</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_preferences</TableCell>
                <TableCell>Functional</TableCell>
                <TableCell>Stores user interface preferences including theme selection, sidebar state, and table configurations</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_locale</TableCell>
                <TableCell>Functional</TableCell>
                <TableCell>Stores your language and regional format preferences</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_analytics</TableCell>
                <TableCell>Analytics</TableCell>
                <TableCell>Anonymised session identifier for aggregated usage statistics (page views, feature adoption)</TableCell>
                <TableCell>26 months</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>amudfin_analytics_consent</TableCell>
                <TableCell>Essential</TableCell>
                <TableCell>Records whether you have accepted or declined analytics cookies</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* 4. Managing Cookies */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. How to Manage Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          You can manage your cookie preferences in the following ways:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="In-platform consent banner"
              secondary="When you first visit AmudFin, a cookie consent banner allows you to accept or decline non-essential cookies. You can change your preferences at any time from the platform settings."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Browser settings"
              secondary="Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies, delete existing cookies, or alert you when a cookie is being set. Note that disabling essential cookies will prevent you from using the platform."
            />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          Instructions for managing cookies in common browsers:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Google Chrome: Settings > Privacy and security > Cookies and other site data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Mozilla Firefox: Settings > Privacy & Security > Cookies and Site Data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Safari: Preferences > Privacy > Manage Website Data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Microsoft Edge: Settings > Cookies and site permissions > Cookies and site data" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Third-Party Cookies */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Third-Party Cookies
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin does not set any third-party cookies. We do not use third-party advertising
          networks, social media tracking pixels, or external analytics services that place their
          own cookies on your device. All cookies listed in Section 3 are first-party cookies set
          directly by the AmudFin platform.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 6. Changes */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Changes to This Cookie Policy
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We may update this Cookie Policy to reflect changes in our cookie usage or applicable law.
          If we introduce new categories of cookies or materially change how we use existing ones,
          we will notify you via the platform and request renewed consent where required. The
          &ldquo;Last updated&rdquo; date at the top of this page indicates the most recent
          revision.
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            For questions about this Cookie Policy, contact us at privacy@amudfin.com.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CookiePolicyPage;
